/**
 * M8a — Admin sidebar navigation + role gates.
 *
 * Mirrors the legacy `js/admin.js` `SECTION_META` + `ADMIN_NAV_GROUPS`
 * + `ADMIN_ONLY_SECTIONS` + `LEGACY_SECTION_REDIRECTS`. Keep this
 * file in sync with the inventory's section table (M8 F-rows).
 */

import type { MePayload } from "@/lib/types";

export type AdminSectionId =
  | "overview"
  | "files"
  | "matches"
  | "live"
  | "performance"
  | "people"
  | "teams"
  | "seasons"
  | "settings";

export type AdminGroupId = "operations" | "tenants" | "platform";

export type AdminSection = {
  id: AdminSectionId;
  label: string;
  /** Two-character glyph displayed left of the label (e.g. "01"). */
  glyph: string;
  /** One-line context shown under the label. */
  kicker: string;
  group: AdminGroupId;
  /** SPA path. The hash fallback is built from this id. */
  path: string;
};

export const ADMIN_SECTIONS: AdminSection[] = [
  // Operations — video ingest, live delivery, storage, and host health
  { id: "overview", label: "Club Health", glyph: "01", kicker: "Overview", group: "operations", path: "/admin/overview" },
  { id: "live", label: "Broadcast", glyph: "02", kicker: "Live", group: "operations", path: "/admin/live" },
  { id: "performance", label: "Video Pipeline", glyph: "03", kicker: "Processing", group: "operations", path: "/admin/performance" },
  { id: "files", label: "Media Files", glyph: "04", kicker: "Storage", group: "operations", path: "/admin/files" },
  // Club setup — shared seasons, squads, access, and team match rows
  { id: "seasons", label: "Season Calendar", glyph: "05", kicker: "Shared seasons", group: "tenants", path: "/admin/seasons" },
  { id: "teams", label: "Squads", glyph: "06", kicker: "Club teams", group: "tenants", path: "/admin/teams" },
  { id: "people", label: "Access", glyph: "07", kicker: "Accounts & invites", group: "tenants", path: "/admin/people" },
  { id: "matches", label: "Match Library", glyph: "08", kicker: "Team videos", group: "tenants", path: "/admin/matches" },
  // Platform — global product branding and delivery settings
  { id: "settings", label: "Settings", glyph: "09", kicker: "Branding", group: "platform", path: "/admin/settings" },
];

export const ADMIN_GROUPS: Array<{ id: AdminGroupId; label: string }> = [
  { id: "operations", label: "Operations" },
  { id: "tenants", label: "Club Setup" },
  { id: "platform", label: "Platform" },
];

/** Sections only global admins see. Team admins + uploaders are
 * redirected away if they hit one of these directly. */
const ADMIN_ONLY_SECTIONS = new Set<AdminSectionId>([
  "overview",
  "files",
  "live",
  "performance",
  "teams",
  "seasons",
  "settings",
]);

/** Legacy URL → current section path. Used by the router to redirect
 * bookmarked / emailed links from the pre-fork repo. */
export const LEGACY_SECTION_REDIRECTS: Record<string, string> = {
  "/admin/streams": "/admin/live",
  "/admin/system": "/admin/performance",
  "/admin/users": "/admin/people",
};

/**
 * Can this user see this admin section?
 *
 * Rules mirror the legacy `js/admin.js` gates:
 *   - Global admin sees everything.
 *   - Team admin (has any membership with `team_admin` role) sees
 *     `matches` + `people` only.
 *   - Uploader (`role` includes `uploader`) sees `matches` only.
 *   - Plain coach / viewer sees nothing — the entire shell hides.
 */
export function canSeeAdminSection(
  section: AdminSectionId,
  me: MePayload | null | undefined,
): boolean {
  if (!me?.user) return false;
  if (me.user.is_global_admin) return true;

  // Team admin = any membership with role `team_admin`. The active
  // scope's effective_role isn't enough — a team admin scoped to a
  // viewer-role team should still see /admin/people on their admin
  // team. We check the full memberships array.
  const isTeamAdmin = me.memberships?.some((m) => m.role === "team_admin");
  if (isTeamAdmin) {
    return section === "matches" || section === "people";
  }

  const roles = me.user.roles ?? [];
  if (roles.includes("uploader")) {
    return section === "matches";
  }

  return false;
}

/** What's the user's best landing page inside /admin? Mirrors the
 * legacy default-landing rules. */
export function defaultAdminLanding(me: MePayload | null | undefined): AdminSectionId {
  if (!me?.user) return "matches";
  if (me.user.is_global_admin) return "overview";
  const isTeamAdmin = me.memberships?.some((m) => m.role === "team_admin");
  if (isTeamAdmin) return "people";
  return "matches";
}

/** True if this user can see ANY admin section. The /admin route
 * gates on this to keep plain coaches/viewers out of the shell. */
export function hasAdminAccess(me: MePayload | null | undefined): boolean {
  if (!me?.user) return false;
  if (me.user.is_global_admin) return true;
  if (me.memberships?.some((m) => m.role === "team_admin")) return true;
  if ((me.user.roles ?? []).includes("uploader")) return true;
  return false;
}

/** Title chrome — "Admin Console" / "Team Admin Console" / "Match
 * Studio" — driven by the user's role. */
export function adminConsoleTitle(me: MePayload | null | undefined): string {
  if (!me?.user) return "Admin";
  if (me.user.is_global_admin) return "Admin Console";
  if (me.memberships?.some((m) => m.role === "team_admin")) return "Team Admin Console";
  if ((me.user.roles ?? []).includes("uploader")) return "Match Studio";
  return "Admin";
}

export const _internal = { ADMIN_ONLY_SECTIONS };
